import React, { useEffect, useState, useMemo } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
    Switch,
    TextField,
    InputAdornment,
    Chip,
    TableSortLabel,
    Pagination,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from "@mui/material";
import { Search as SearchIcon, Visibility, VisibilityOff, ArrowUpward, ArrowDownward } from "@mui/icons-material";
import { apiService } from "../services/api";
import { Media } from "../types";
import { statsStore, supplierStore } from "../stores";

const MediaVisibilityManagement: React.FC = () => {
    const [mediaList, setMediaList] = useState<Media[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [updatingIds, setUpdatingIds] = useState<Set<number>>(new Set());
    const [sortField, setSortField] = useState<'id' | 'name' | 'supplier'>('id');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(50);

    useEffect(() => {
        fetchMediaList();
    }, []);

    const fetchMediaList = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await apiService.getMediaVisibilityList();
            // Метод getMediaVisibilityList уже нормализует данные, но на всякий случай проверяем
            const mediaArray = Array.isArray(data) ? data : [];
            setMediaList(mediaArray);
        } catch (err: any) {
            console.error("Ошибка загрузки списка медиа:", err);
            setError(err.response?.data?.message || "Ошибка загрузки списка медиа");
        } finally {
            setLoading(false);
        }
    };

    const handleToggleVisibility = async (mediaId: number, currentValue: boolean) => {
        try {
            setUpdatingIds(prev => new Set(prev).add(mediaId));
            await apiService.updateMediaVisibility(mediaId, !currentValue);
            
            // Обновляем локальное состояние
            setMediaList(prev => prev.map(media => 
                media.id === mediaId 
                    ? { ...media, isVisibleInStats: !currentValue }
                    : media
            ));
            
            // Перезагружаем статистику, чтобы изменения применились немедленно
            const currentSupplier = supplierStore.currentSupplier;
            if (currentSupplier) {
                try {
                    await statsStore.refreshAllData(currentSupplier.id);
                    console.log("✅ Статистика обновлена после изменения видимости медиа");
                } catch (refreshError) {
                    console.error("Ошибка обновления статистики:", refreshError);
                    // Не показываем ошибку пользователю, так как основное действие выполнено
                }
            }
        } catch (err: any) {
            console.error("Ошибка обновления видимости:", err);
            setError(err.response?.data?.message || "Ошибка обновления видимости");
        } finally {
            setUpdatingIds(prev => {
                const next = new Set(prev);
                next.delete(mediaId);
                return next;
            });
        }
    };

    const handleSort = (field: 'id' | 'name' | 'supplier') => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
        setPage(1); // Сбрасываем на первую страницу при изменении сортировки
    };

    const filteredMedia = useMemo(() => {
        let filtered = mediaList.filter(media => {
            if (!searchTerm) return true;
            
            const searchLower = searchTerm.toLowerCase();
            
            // Поиск по названию медиа
            if (media.name && media.name.toLowerCase().includes(searchLower)) return true;
            
            // Поиск по иерархии (если есть)
            if (media.hierarchy && media.hierarchy.toLowerCase().includes(searchLower)) return true;
            
            // Поиск по названию сериала (grandparent)
            if (media.grandparent?.name && media.grandparent.name.toLowerCase().includes(searchLower)) return true;
            
            // Поиск по названию сезона (parent)
            if (media.parent?.name && media.parent.name.toLowerCase().includes(searchLower)) return true;
            
            // Поиск по ID
            if (media.id.toString().includes(searchTerm)) return true;
            
            return false;
        });

        // Сортировка
        filtered.sort((a, b) => {
            let aValue: any;
            let bValue: any;

            switch (sortField) {
                case 'id':
                    aValue = a.id;
                    bValue = b.id;
                    break;
                case 'name':
                    // Используем иерархию или название для сортировки
                    aValue = a.hierarchy || a.name || '';
                    bValue = b.hierarchy || b.name || '';
                    break;
                case 'supplier':
                    aValue = a.supplier?.name || a.supplierId || '';
                    bValue = b.supplier?.name || b.supplierId || '';
                    break;
                default:
                    return 0;
            }

            if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });

        return filtered;
    }, [mediaList, searchTerm, sortField, sortDirection]);

    // Пагинация
    const paginatedMedia = useMemo(() => {
        const startIndex = (page - 1) * rowsPerPage;
        return filteredMedia.slice(startIndex, startIndex + rowsPerPage);
    }, [filteredMedia, page, rowsPerPage]);

    const totalPages = Math.ceil(filteredMedia.length / rowsPerPage);

    const visibleCount = mediaList.filter(m => m.isVisibleInStats !== false).length;
    const hiddenCount = mediaList.length - visibleCount;

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error && mediaList.length === 0) {
        return <Alert severity="error">{error}</Alert>;
    }

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" component="h1" gutterBottom>
                Управление видимостью медиа в статистике
            </Typography>
            
            <Alert severity="info" sx={{ mb: 3, mt: 2 }}>
                Здесь вы можете управлять видимостью медиа в статистике для поставщиков. 
                Скрытые медиа не будут отображаться в статистике поставщиков, но будут видны в админке.
            </Alert>

            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <TextField
                            placeholder="Поиск по названию медиа..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            size="small"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ width: 300 }}
                        />
                        <Box display="flex" gap={2}>
                            <Chip 
                                icon={<Visibility />} 
                                label={`Видимые: ${visibleCount}`} 
                                color="success" 
                                variant="outlined" 
                            />
                            <Chip 
                                icon={<VisibilityOff />} 
                                label={`Скрытые: ${hiddenCount}`} 
                                color="default" 
                                variant="outlined" 
                            />
                        </Box>
                    </Box>
                </CardContent>
            </Card>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <Card>
                <CardContent>
                    <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ width: '80px' }}>ID</TableCell>
                                    <TableCell sx={{ minWidth: '400px', maxWidth: '600px' }}>Название медиа</TableCell>
                                    <TableCell sx={{ width: '150px' }}>Поставщик</TableCell>
                                    <TableCell align="center" sx={{ width: '200px' }}>Видимость в статистике</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredMedia.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center">
                                            <Typography color="text.secondary">
                                                {searchTerm ? "Медиа не найдены" : "Нет медиа"}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    paginatedMedia.map((media) => (
                                        <TableRow key={media.id}>
                                            <TableCell>{media.id}</TableCell>
                                            <TableCell>
                                                <Box sx={{ maxWidth: '100%', wordBreak: 'break-word' }}>
                                                    {/* Компактное отображение иерархии */}
                                                    {(() => {
                                                        // Используем hierarchy если есть, иначе строим сами
                                                        if (media.hierarchy && media.hierarchy !== media.name) {
                                                            return (
                                                                <Typography 
                                                                    variant="body1" 
                                                                    sx={{ 
                                                                        fontWeight: "medium",
                                                                        wordBreak: 'break-word',
                                                                        whiteSpace: 'normal'
                                                                    }}
                                                                >
                                                                    {media.hierarchy}
                                                                </Typography>
                                                            );
                                                        }
                                                        
                                                        // Строим иерархию вручную - ВСЕГДА показываем полную иерархию
                                                        const parts: string[] = [];
                                                        if (media.grandparent) {
                                                            parts.push(media.grandparent.name);
                                                        }
                                                        if (media.parent && media.grandparent) {
                                                            parts.push(media.parent.name);
                                                        }
                                                        // ВСЕГДА добавляем название эпизода, если оно есть
                                                        if (media.name && media.name !== "Без названия") {
                                                            parts.push(media.name);
                                                        }
                                                        
                                                        if (parts.length > 1) {
                                                            return (
                                                                <Typography 
                                                                    variant="body1" 
                                                                    sx={{ 
                                                                        fontWeight: "medium",
                                                                        wordBreak: 'break-word',
                                                                        whiteSpace: 'normal'
                                                                    }}
                                                                >
                                                                    {parts.join(' > ')}
                                                                </Typography>
                                                            );
                                                        }
                                                        
                                                        return (
                                                            <Typography 
                                                                variant="body1" 
                                                                sx={{ 
                                                                    fontWeight: "medium",
                                                                    wordBreak: 'break-word',
                                                                    whiteSpace: 'normal'
                                                                }}
                                                            >
                                                                {media.name || "Без названия"}
                                                            </Typography>
                                                        );
                                                    })()}
                                                </Box>
                                            </TableCell>
                                            <TableCell>{media.supplier?.name || media.supplierId || "Нет"}</TableCell>
                                            <TableCell align="center">
                                                {updatingIds.has(media.id) ? (
                                                    <CircularProgress size={24} />
                                                ) : (
                                                    <Switch
                                                        checked={media.isVisibleInStats !== false}
                                                        onChange={() => 
                                                            handleToggleVisibility(
                                                                media.id, 
                                                                media.isVisibleInStats !== false
                                                            )
                                                        }
                                                        color="primary"
                                                    />
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {filteredMedia.length > 0 && (
                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                            <Typography variant="caption" color="text.secondary">
                                Показано {paginatedMedia.length} из {filteredMedia.length} медиа (всего: {mediaList.length})
                        </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <FormControl size="small" sx={{ minWidth: 120 }}>
                                    <InputLabel>На странице</InputLabel>
                                    <Select
                                        value={rowsPerPage}
                                        label="На странице"
                                        onChange={(e) => {
                                            setRowsPerPage(Number(e.target.value));
                                            setPage(1);
                                        }}
                                    >
                                        <MenuItem value={25}>25</MenuItem>
                                        <MenuItem value={50}>50</MenuItem>
                                        <MenuItem value={100}>100</MenuItem>
                                        <MenuItem value={200}>200</MenuItem>
                                    </Select>
                                </FormControl>
                                {totalPages > 1 && (
                                    <Pagination
                                        count={totalPages}
                                        page={page}
                                        onChange={(_, value) => setPage(value)}
                                        color="primary"
                                        size="small"
                                        showFirstButton
                                        showLastButton
                                    />
                                )}
                            </Box>
                        </Box>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default MediaVisibilityManagement;

